import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, X, Zap, Shield, Sparkles, ArrowRight } from "lucide-react";

const ROWS = [
  { label: "Phone HLR + carrier lookup",       us: true,  abstract: true,  twilio: true,  ipqs: false },
  { label: "Email deliverability (MX + SMTP)", us: true,  abstract: true,  twilio: false, ipqs: false },
  { label: "IP threat intel (Tor/VPN/proxy)",  us: true,  abstract: true,  twilio: false, ipqs: true  },
  { label: "Unified credit system (1 = any)",  us: true,  abstract: false, twilio: false, ipqs: false },
  { label: "MCP connector for AI agents",      us: true,  abstract: false, twilio: false, ipqs: false },
  { label: "Credits roll over 3 months",       us: true,  abstract: false, twilio: false, ipqs: false },
  { label: "25 free browser tools",            us: true,  abstract: true,  twilio: false, ipqs: true  },
  { label: "Sub-300ms p50 latency",            us: true,  abstract: false, twilio: true,  ipqs: true  },
  { label: "Free tier (no card)",              us: "500", abstract: "100", twilio: "trial", ipqs: "no" },
  { label: "Starter monthly price",            us: "$99", abstract: "$59", twilio: "pay-as-you-go", ipqs: "$99" },
];

const Head = ({ name, tag, highlight }) => (
  <th className={`p-4 text-left align-bottom ${highlight ? "bg-cyan-500/5 border-l border-r border-cyan-500/20" : ""}`}>
    <div className={`font-display font-bold ${highlight ? "text-cyan-signal text-lg" : "text-slate-300"}`}>{name}</div>
    {tag && <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">{tag}</div>}
  </th>
);

const Cell = ({ v, highlight }) => {
  const base = "p-4 text-center border-t border-slate-800";
  const hi = highlight ? " bg-cyan-500/5 border-l border-r border-cyan-500/20" : "";
  if (v === true)  return <td className={base + hi}><Check className="w-5 h-5 text-emerald-400 mx-auto" /></td>;
  if (v === false) return <td className={base + hi}><X className="w-5 h-5 text-slate-600 mx-auto" /></td>;
  return <td className={base + hi}><span className="font-mono text-sm text-slate-200">{v}</span></td>;
};

export default function Compare() {
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-70 pointer-events-none" />
      <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[700px] h-[380px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mb-10 reveal">
          <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Comparison</span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tighter">
            The Good Guys vs. everyone else.
          </h1>
          <p className="mt-4 text-slate-400 text-lg leading-relaxed">
            Honest side-by-side against the three verification tools fraud teams stitch together today: Abstract API, Twilio Lookup, and IPQualityScore. One credit, any validator, MCP-native — that's the difference.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-xl overflow-x-auto" data-testid="comparison-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr>
                <th className="p-4 text-left text-xs font-mono uppercase tracking-widest text-slate-500 w-2/5">Feature</th>
                <Head name="The Good Guys" tag="us" highlight />
                <Head name="Abstract API" tag="competitor" />
                <Head name="Twilio Lookup" tag="competitor" />
                <Head name="IPQualityScore" tag="competitor" />
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.label} data-testid={`compare-row-${r.label.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}>
                  <td className="p-4 border-t border-slate-800 text-slate-200 font-medium">{r.label}</td>
                  <Cell v={r.us}       highlight />
                  <Cell v={r.abstract} />
                  <Cell v={r.twilio}   />
                  <Cell v={r.ipqs}     />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {[
            { icon: Zap,      title: "Unified pricing",  desc: "One credit covers any validator. Stop juggling three invoices." },
            { icon: Shield,   title: "Deterministic schema", desc: "Same response shape, forever. No breaking changes when a vendor pivots." },
            { icon: Sparkles, title: "MCP-native",       desc: "AI agents plug in via OAuth 2.1 — the only vendor on this table that supports it." },
          ].map((f) => (
            <div key={f.title} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
              <f.icon className="w-5 h-5 text-cyan-signal mb-3" />
              <p className="font-display font-semibold">{f.title}</p>
              <p className="text-sm text-slate-400 mt-1">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-slate-900 to-cyan-950/30 p-8 sm:p-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Ready to consolidate?</h2>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">500 free credits and code <span className="font-mono text-cyan-signal">LAUNCH50</span> for another 500. Kick the tires with your worst suspicious signups — see what we catch that the other three miss.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="compare-cta-signup"><Button size="lg" className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold">Start free · 500 credits <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/launch" data-testid="compare-cta-launch"><Button size="lg" variant="outline" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800">Claim launch bonus</Button></Link>
          </div>
        </div>

        <p className="text-xs text-slate-600 mt-8 text-center max-w-2xl mx-auto">Comparison based on publicly documented feature sets and starter-tier pricing as of Feb 2026. Trademarks belong to their respective owners. Have a correction? Email team@thegoodguys.app.</p>
      </div>
    </div>
  );
}
